import React, { MouseEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import { Bold } from '@lib/styles';
import {
  Box,
  ButtonSize,
  CycleForewardIcon,
  GridItem,
  IconSize,
  OSmallParagraph,
  PrimaryButton,
  SecondaryButton,
  defaultColors,
  OParagraph,
} from '@lifechurch/react-ion';
import { formatDate, recurrenceType, serviceTimeHasPast } from './Utils';
import { ServiceFieldsFragment } from '@generated/types';
import { useTranslation } from 'next-i18next';

interface ServiceTimeProps {
  service: ServiceFieldsFragment;
  isEditable: boolean;
  isPastService?: boolean;
  isOnboarding?: boolean;
}

interface ContentProps extends ServiceTimeProps {
  ref?: React.Ref<HTMLElement>;
  href?: string;
  isPastService?: boolean;
  onClick?: () => void;
}

interface StyledGridItemProps {
  isEditable: boolean;
  ref?: React.Ref<HTMLElement>;
  href?: string;
  onClick?: () => void;
}

const ServiceTitle = styled(OSmallParagraph)`
  margin-inline-end: ${props => props.theme.space[3]};
`;

const TimeAndDate = styled.div`
  margin-inline-start: 0;
  margin-block-start: 0;
  margin-block-end: 0;
  margin-inline-end: 16px;
  display: flex;
  align-items: center;
`;

const uneditableCss = css`
  ${TimeAndDate},
  ${ServiceTitle} {
    color: ${props => props.theme.colors.gray50};
  }
`;

const editableCss = css`
  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.colors.action.light};

    ${TimeAndDate} {
      color: #4facf4;
    }

    ${Bold} {
      color: #4facf4;
    }

    &:after {
      display: none;
    }

    &:not(:first-child) {
      box-shadow: 0px -1px ${props => props.theme.colors.action.light};
    }
  }
`;

const StyledGridItem = styled(GridItem)<StyledGridItemProps>`
  padding: ${props => props.theme.space[5]}px ${props => props.theme.space[5]}px
    ${props => props.theme.space[5] + 1}px ${props => props.theme.space[5]}px;
  position: relative;

  &:not(:last-child):after {
    position: absolute;
    bottom: 0;
    left: ${props => props.theme.space[5]}px;
    right: ${props => props.theme.space[5]}px;
    display: block;
    height: 1px;
    content: '';
    background-color: ${props => props.theme.colors.gray10};
  }

  ${props => (!props.isEditable ? uneditableCss : editableCss)};
`;

const PastServiceLinkContainer = styled(Box)`
  grid-column: span 12;

  &:not(:last-child):after {
    position: relative;
    margin: 0 16px;
    display: block;
    height: 1px;
    content: '';
    background-color: ${props => props.theme.colors.gray10};
  }

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.colors.action.light};

    &:not(:first-child) {
      box-shadow: 0px -1px ${props => props.theme.colors.action.light};
    }
  }
`;
const NoContentParagraph = styled(OSmallParagraph)`
  color: ${props => props.theme.colors.gray50};
`;

const Content: React.FC<ContentProps> = React.forwardRef(
  ({ service, isEditable, href, isPastService, onClick, isOnboarding }: ContentProps, ref) => {
    const dateTime = formatDate(service.scheduleTime);
    const hasVideo = (service?.content?.video?.source ?? '') !== '';
    const hasContent = !!service?.content;
    const router = useRouter();
    const { t } = useTranslation(['services', 'common']);
    const recurrence = recurrenceType(service.recurrence, t);

    const handleClick = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      router.push(`/service/${service.id}/content`);
    };

    return (
      <StyledGridItem
        p={3}
        gridColumn={'span 12'}
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        data-testid={isEditable ? `serviceTime-editable` : `serviceTime-uneditable`}
        isEditable={isEditable}
        ref={ref}
        href={href}
        onClick={onClick}
      >
        <Box
          display={'inline-flex'}
          alignItems={'center'}
          width={'100%'}
          justifyContent={'space-between'}
        >
          <Box>
            <TimeAndDate>
              <OParagraph display={'inline-block'}>
                <Bold data-testid={`serviceTime-${service.id}`}>{dateTime[0]}</Bold>
                &nbsp;{dateTime[1]}
              </OParagraph>
              {recurrence && (
                <Box display={'inline-flex'} alignItems={'center'} marginInlineStart={3}>
                  <CycleForewardIcon size={IconSize.SM} color={defaultColors.gray50} />
                  <OSmallParagraph
                    display={'inline'}
                    color={defaultColors.gray50}
                    marginInlineStart={2}
                  >
                    {recurrence}
                  </OSmallParagraph>
                </Box>
              )}
            </TimeAndDate>
            {service.content && (
              <ServiceTitle data-testid='serviceContent-title' marginInlineEnd={5}>
                {service.content.title}
              </ServiceTitle>
            )}
          </Box>
        </Box>
        <Box display={'inline-flex'} alignItems={'center'} flexShrink={0}>
          {!hasVideo && !isPastService && hasContent && (
            <OSmallParagraph
              marginInlineEnd={3}
              px={3}
              py={2}
              bg={defaultColors.warning.light}
              color={defaultColors.warning.dark}
              data-testid='needsVideoLabel'
            >
              {t('needs_video')}
            </OSmallParagraph>
          )}
          {!isPastService && (
            <Link href='/service/[id]/content' as={`/service/${service.id}/content`}>
              {service.content ? (
                <SecondaryButton
                  size={ButtonSize.SM}
                  data-testid={`editContentButton-${service.id}`}
                >
                  {t('edit_content')}
                </SecondaryButton>
              ) : (
                <PrimaryButton
                  size={ButtonSize.SM}
                  data-testid={`addContentButton-${service.id}`}
                  pulsing={isOnboarding}
                >
                  {t('add_content')}
                </PrimaryButton>
              )}
            </Link>
          )}
          {isPastService && !service.content && (
            <NoContentParagraph>{t('no_content')}</NoContentParagraph>
          )}
          {isPastService && service.content && (
            <Box onClick={handleClick}>
              <SecondaryButton size={ButtonSize.SM} data-testid={`viewContentButton-${service.id}`}>
                {t('view_content')}
              </SecondaryButton>
            </Box>
          )}
        </Box>
      </StyledGridItem>
    );
  }
);
Content.displayName = 'ServiceContent';

const ServiceTime: React.FC<ServiceTimeProps> = ({
  service,
  isEditable,
  isOnboarding,
}: ServiceTimeProps) => {
  const isPastService = serviceTimeHasPast(service.endTime || service.scheduleTime);
  const router = useRouter();

  const handleClick = (e: MouseEvent): void => {
    e.preventDefault();
    router.push(`/service/[id]`, `/service/${service.id}`);
  };

  return (
    <>
      {isPastService && (
        <PastServiceLinkContainer onClick={handleClick} data-testid='pastServiceLinkContainer'>
          <Content service={service} isEditable={isEditable} isPastService={isPastService} />
        </PastServiceLinkContainer>
      )}
      {isEditable && !isPastService && (
        <Link href='/service/[id]' as={`/service/${service.id}`} passHref>
          <Content
            service={service}
            isEditable={isEditable}
            isPastService={isPastService}
            isOnboarding={isOnboarding}
          />
        </Link>
      )}
      {!isEditable && !isPastService && (
        <Content service={service} isEditable={isEditable} isPastService={isPastService} />
      )}
    </>
  );
};

export default ServiceTime;
